import { useCallback, useEffect, useState } from 'react';
import { MdPhoneIphone, MdEdit, MdBadge, MdMergeType, MdUndo } from 'react-icons/md';
import { Drawer, DialogBody, DialogFooter } from '../ui/Dialog';
import { Badge, Button, Field, Skeleton } from '../ui';
import { personUsage, undoMerge, updatePerson } from '../../api/addressBook';
import { messaggioErrore } from '../../utils/apiError';

const ORIGINE_NUMERO = {
  whatsapp: { testo: 'Numero da WhatsApp', tone: 'success' },
  manual: { testo: 'Numero inserito a mano', tone: 'info' },
  none: { testo: 'Numero non disponibile', tone: 'warning' },
};

/** Scheda della persona: le sue identità WhatsApp, dove è usata, e le note.
 *  Il numero mostra sempre da dove viene: nessun numero presunto. */
export default function PersonDrawer({ person, open, onClose, connections, onChanged, onEditIdentity, onMerge }) {
  const [uso, setUso] = useState(null);
  const [bozza, setBozza] = useState(null);
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState('');

  const carica = useCallback(async () => {
    if (!person) return;
    setUso(null);
    try {
      setUso(await personUsage(person.id));
    } catch (e) {
      setErrore(messaggioErrore(e));
    }
  }, [person]);

  useEffect(() => {
    setBozza(null); setErrore('');
    if (open) carica();
  }, [open, carica]);

  if (!person) return null;

  const nomeAccount = id => {
    const c = connections.find(s => s.id === id);
    return c ? (c.phone_number || c.session_id) : `account ${id}`;
  };

  const salva = async () => {
    setBusy(true); setErrore('');
    try {
      await updatePerson(person.id, bozza);
      setBozza(null);
      onChanged?.();
    } catch (e) {
      setErrore(messaggioErrore(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} label={person.display_name}
      description={`${person.identities?.length || 0} identità WhatsApp`}>
      <DialogBody className="space-y-5">
        {errore && <p role="alert" className="rounded bg-danger-bg px-3 py-2 text-sm text-danger">{errore}</p>}

        <section>
          <h3 className="mb-2 text-sm font-semibold text-ink">Identità collegate</h3>
          <ul className="space-y-2">
            {(person.identities || []).map(identita => {
              const origine = ORIGINE_NUMERO[identita.phone_source] || ORIGINE_NUMERO.none;
              return (
                <li key={identita.id} className="rounded border border-line p-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="flex items-center gap-1.5 text-sm text-ink">
                        <MdPhoneIphone size={14} aria-hidden className="shrink-0 text-muted" />
                        <span className="tabular-nums">{identita.phone || 'Numero non disponibile'}</span>
                      </p>
                      <p className="mt-0.5 truncate text-xs text-muted" title={identita.whatsapp_id}>
                        {nomeAccount(identita.connection)} · {identita.whatsapp_id}
                      </p>
                    </div>
                    <Button size="sm" icon={MdEdit} onClick={() => onEditIdentity?.(identita)}>Modifica</Button>
                  </div>
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    <Badge tone={origine.tone}>{origine.testo}</Badge>
                    {identita.archived && <Badge>Archiviata</Badge>}
                  </div>
                </li>
              );
            })}
            {!person.identities?.length && (
              <li className="rounded border border-dashed border-line p-3 text-sm text-muted">
                Nessuna identità WhatsApp: questa persona non è raggiungibile da nessun account.
              </li>
            )}
          </ul>
        </section>

        <section>
          <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-ink">
            <MdBadge size={16} aria-hidden /> Dove è usata
          </h3>
          {!uso ? <Skeleton className="h-16 w-full" /> : (
            <ul className="space-y-1 text-sm text-ink">
              <li>{uso.groups.length} gruppi rubrica{uso.groups.length > 0 && `: ${uso.groups.map(g => g.name).join(', ')}`}</li>
              <li>{uso.reminders.length} promemoria</li>
              <li>{uso.workflows.length} automazioni</li>
            </ul>
          )}
          <p className="mt-1 text-xs text-muted">
            Eliminando le sue identità queste voci smettono di raggiungerla.
          </p>
        </section>

        <section className="space-y-2">
          <h3 className="text-sm font-semibold text-ink">Dati della persona</h3>
          {bozza ? (
            <>
              <Field label="Nome" value={bozza.display_name}
                onChange={e => setBozza({ ...bozza, display_name: e.target.value })} />
              <Field label="Nome breve" value={bozza.short_name}
                onChange={e => setBozza({ ...bozza, short_name: e.target.value })} />
              <Field label="Categoria" value={bozza.category}
                onChange={e => setBozza({ ...bozza, category: e.target.value })} />
              <Field label="Note">
                {props => (
                  <textarea {...props} rows={4} value={bozza.notes}
                    onChange={e => setBozza({ ...bozza, notes: e.target.value })} />
                )}
              </Field>
            </>
          ) : (
            <dl className="space-y-1 text-sm">
              {[['Nome breve', person.short_name], ['Categoria', person.category], ['Note', person.notes]]
                .map(([etichetta, valore]) => (
                  <div key={etichetta} className="flex gap-2">
                    <dt className="w-28 shrink-0 text-muted">{etichetta}</dt>
                    <dd className="whitespace-pre-wrap text-ink">{valore || '—'}</dd>
                  </div>
                ))}
            </dl>
          )}
        </section>
      </DialogBody>

      <DialogFooter>
        {!bozza && (
          <>
            <Button icon={MdMergeType} className="mr-auto" onClick={() => onMerge?.(person)}>
              Unisci con…
            </Button>
            {uso?.last_merge && (
              <Button icon={MdUndo} loading={busy}
                onClick={async () => {
                  setBusy(true);
                  try { await undoMerge(uso.last_merge); await carica(); onChanged?.(); }
                  catch (e) { setErrore(messaggioErrore(e)); }
                  finally { setBusy(false); }
                }}>
                Annulla unione
              </Button>
            )}
          </>
        )}
        {bozza ? (
          <>
            <Button variant="ghost" onClick={() => setBozza(null)}>Annulla</Button>
            <Button variant="primary" loading={busy} onClick={salva}>Salva</Button>
          </>
        ) : (
          <Button icon={MdEdit} onClick={() => setBozza({
            display_name: person.display_name, short_name: person.short_name || '',
            category: person.category || '', notes: person.notes || '',
          })}>
            Modifica persona
          </Button>
        )}
      </DialogFooter>
    </Drawer>
  );
}
